import * as TabsPrimitive from '@radix-ui/react-tabs';
import { type ComponentProps, useCallback, useEffect, useRef } from 'react';

import { cn } from '@/design-system/lib/cn';

/**
 * Abas sobre Radix Tabs (setas, Home e End; `role=tab` e `aria-selected`). `segmented`
 * é o trilho cinza das visões da escala; `card` é o trilho branco das seções do perfil.
 */
export const Tabs = TabsPrimitive.Root;

export type TabsListProps = ComponentProps<typeof TabsPrimitive.List> & { variant?: 'segmented' | 'card' };

/** Rola o trilho (só ele, não a página) até centralizar a aba ativa; nas pontas o navegador limita. */
function centerActiveTab(list: HTMLElement, immediate = false) {
  const tab = list.querySelector<HTMLElement>('[role="tab"][data-state="active"]');
  if (!tab || list.scrollWidth <= list.clientWidth) return;

  const box = list.getBoundingClientRect();
  const at = tab.getBoundingClientRect();
  const left = list.scrollLeft + (at.left - box.left) - (box.width - at.width) / 2;
  const reduced = immediate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  list.scrollTo?.({ left, behavior: reduced ? 'auto' : 'smooth' });
}

export function TabsList({ className, variant = 'segmented', ref, ...props }: TabsListProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    requestAnimationFrame(() => centerActiveTab(list, true));
    const observer = new MutationObserver(() => centerActiveTab(list));
    observer.observe(list, { attributes: true, attributeFilter: ['data-state'], subtree: true });

    const handleResize = () => centerActiveTab(list, true);
    window.addEventListener('resize', handleResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <TabsPrimitive.List
      ref={setRef}
      data-variant={variant}
      className={cn(
        'group/tabs scrollbar-none inline-flex max-w-full min-w-0 gap-0.5 overflow-x-auto',
        variant === 'segmented'
          ? 'rounded-control bg-line-input p-0.75'
          : 'rounded-block border border-line-card bg-white p-1',
        className
      )}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-control-sm px-3 text-center font-medium whitespace-nowrap text-ink-subtle transition-colors',
        '[&_svg]:size-3.5 [&_svg]:stroke-[1.8]',
        'group-data-[variant=segmented]/tabs:h-8 group-data-[variant=segmented]/tabs:text-small',
        'group-data-[variant=segmented]/tabs:data-[state=active]:bg-white group-data-[variant=segmented]/tabs:data-[state=active]:text-ink group-data-[variant=segmented]/tabs:data-[state=active]:shadow-raised',
        'group-data-[variant=card]/tabs:h-9 group-data-[variant=card]/tabs:px-3.5 group-data-[variant=card]/tabs:text-control group-data-[variant=card]/tabs:font-semibold',
        'group-data-[variant=card]/tabs:data-[state=active]:bg-primary-soft group-data-[variant=card]/tabs:data-[state=active]:text-primary-hover',
        'hover:text-ink',
        className
      )}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content className={cn('outline-none', className)} {...props} />;
}
